import { useCallback, useContext } from 'react';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import { useSession } from 'src/hooks/useSession';
import { useGetURLForFileUploadMutation } from 'src/store/services';
import { FileObject, TokenApiHttpError } from 'src/types';
import {
  DEFAULT_CHAT_ID,
  MAX_FILE_SIZE,
  MAX_NUMBER_OF_FILES,
  ZIP_FORMATS,
} from 'src/constants';
import { toast } from 'react-toastify';
import { getRandomFileId } from 'src/utils';
import { useDeleteAttachment } from '../inputBoxHooks/useDeleteAttachment';
import { useConversationParams } from '../conversationHooks';
import { usePollingFileStatus } from 'src/hooks';

const COMMON_ERROR_MESSAGE =
  ' File(s) failed to upload. Try again or check the file type and size limits.';
const COMMON_ERROR_MESSAGE_SINGLE_FILE = 'Unable to process the file';
const MAX_SIZE_ERROR_MESSAGE = `Upload multiple files (up to ${MAX_NUMBER_OF_FILES}) at once`;
const MAX_PASTED_SIZE_ERROR_MESSAGE = 'Pasted text limit reached';

export const useAttachFile = () => {
  const {
    setAttachmentLoading,
    setThreadInputBoxTextError,
    setFileInfoToArray,
    setThreadInputBoxFiles,
    updateFileInfo,
    threadInputBoxFiles,
    convertedFromTextFilesLength,
  } = useContext(ThreadInputBoxContext);
  const {
    appUser: { user_id },
  } = useSession();
  const { pollingFileStatus } = usePollingFileStatus();
  const { currentConversationId } = useConversationParams();
  const [getFileUploadUrl] = useGetURLForFileUploadMutation();
  const { onDeleteAllAttachments } = useDeleteAttachment();

  const setError = useCallback(
    (message: string) => {
      setThreadInputBoxTextError(message);
    },
    [setThreadInputBoxTextError],
  );

  const addFile = useCallback(
    async (
      fileId: string,
      filesObject: {
        [key: string]: FileObject | null;
      },
      shouldShowImageTooltip?: boolean,
    ) => {
      const file = filesObject[fileId]?.file as File;
      setFileInfoToArray(fileId, {
        ...filesObject[fileId],
        isLoading: true,
      });

      if (ZIP_FORMATS.includes(file.type)) {
        const errorMessage = 'File type not supported';
        setFileInfoToArray(fileId, {
          isLoading: false,
          errorMessage: errorMessage,
        });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        const errorMessage =
          'File size exceeds plan limits. Please use a smaller file';
        setFileInfoToArray(fileId, {
          isLoading: false,
          errorMessage: errorMessage,
        });
        return;
      }

      try {
        const result = await getFileUploadUrl({
          user_id,
          filename: file.name.toLocaleLowerCase(),
          content_type: file.type,
          apiContext: {
            user_id,
            conversation_id:
              currentConversationId === DEFAULT_CHAT_ID
                ? ''
                : currentConversationId,
          },
        }).unwrap();

        if (!!result) {
          const formData = new FormData();
          for (const [key, value] of Object.entries(result.form_data.fields)) {
            formData.append(key, value);
          }
          formData.append('file', file);

          const uploadFileUrl = result.form_data?.url;

          // todo refactoring with new hook usage
          await fetch(uploadFileUrl, {
            method: 'POST',
            body: formData,
            mode: 'cors',
          })
            .then(async (response) => {
              if (response.ok) {
                const res = await pollingFileStatus({
                  file_id: result.filename,
                });
                if (res) {
                  if (res.completed.length > 0) {
                    updateFileInfo(
                      fileId,
                      {
                        ...filesObject[fileId],
                        isLoading: false,
                        convertedFileName: result.filename,
                      },
                      shouldShowImageTooltip,
                    );
                  } else {
                    if (res.failed.length > 0 || res.in_progress.length === 0) {
                      updateFileInfo(fileId, {
                        ...filesObject[fileId],
                        isLoading: false,
                        convertedFileName: result.filename,
                        errorMessage:
                          res.failed[0]?.message ||
                          res.in_progress[0]?.message ||
                          COMMON_ERROR_MESSAGE_SINGLE_FILE,
                      });
                    }
                  }
                } else {
                  updateFileInfo(fileId, {
                    ...filesObject[fileId],
                    isLoading: false,
                    convertedFileName: result.filename,
                  });
                }
              } else {
                // todo check errors here
                updateFileInfo(fileId, {
                  isLoading: false,
                  errorMessage: COMMON_ERROR_MESSAGE_SINGLE_FILE,
                });
              }
            })
            .catch(() => {
              // todo check errors here
              updateFileInfo(fileId, {
                isLoading: false,
                errorMessage: COMMON_ERROR_MESSAGE_SINGLE_FILE,
              });
            });
        }
      } catch (error) {
        updateFileInfo(fileId, {
          isLoading: false,
          errorMessage:
            (error as TokenApiHttpError)?.data?.detail ||
            COMMON_ERROR_MESSAGE_SINGLE_FILE,
        });
      }
    },
    [
      user_id,
      getFileUploadUrl,
      setFileInfoToArray,
      updateFileInfo,
      currentConversationId,
      pollingFileStatus,
    ],
  );

  const sendReceivedFile = useCallback(
    async (url: string) => {
      const urlParts = new URL(url);
      const filePath = urlParts.pathname;
      const fileName = filePath.split('/').pop()?.toLocaleLowerCase();

      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const file = new File([blob], fileName || 'image.jpg', {
            type: `image/${fileName?.split('.')[1].toLowerCase()}`,
            lastModified: new Date().getTime(),
          });
          onDeleteAllAttachments();
          setAttachmentLoading(true);
          const fileId = getRandomFileId();
          const fileObject = {
            file: file,
            isLoading: false,
            errorMessage: null,
            isEdit: true,
          };
          setFileInfoToArray(fileId, fileObject);

          if (file.size > MAX_FILE_SIZE) {
            // todo check this case
            const errorMessage =
              'Sorry, the attached image is too large to process. Please upload a smaller file and try again.';
            setError(errorMessage);
            toast.error(errorMessage);
            setAttachmentLoading(false);
          } else {
            setAttachmentLoading(false);
          }
        });
    },
    [
      setError,
      setAttachmentLoading,
      setFileInfoToArray,
      onDeleteAllAttachments,
    ],
  );

  const handleUploadedFiles = useCallback(
    async (files: FileList | File[]) => {
      const numberOfExistingFiles = !!threadInputBoxFiles
        ? Object.keys(threadInputBoxFiles).length - convertedFromTextFilesLength
        : 0;

      let filesObject: Record<string, FileObject> = {};
      const fileArray = Array.from(files);
      const totalFilesLength = numberOfExistingFiles + fileArray.length;
      const uploadedFilesArray =
        totalFilesLength > MAX_NUMBER_OF_FILES
          ? fileArray.slice(0, MAX_NUMBER_OF_FILES - numberOfExistingFiles)
          : fileArray;

      if (totalFilesLength > MAX_NUMBER_OF_FILES) {
        toast.error(MAX_SIZE_ERROR_MESSAGE);
      }

      if (uploadedFilesArray && uploadedFilesArray.length > 0) {
        try {
          setAttachmentLoading(true);
          uploadedFilesArray.forEach((file) => {
            const fileId = getRandomFileId();
            filesObject = {
              ...filesObject,
              [fileId]: {
                file: file,
                isLoading: false,
                errorMessage: null,
              },
            };
          });

          const newFiles = { ...threadInputBoxFiles, ...filesObject };
          setThreadInputBoxFiles(newFiles);
          await Promise.allSettled(
            Object.keys(filesObject).map((key, index, array) => {
              const showImageTooltip =
                array.length === 1 &&
                index === 0 &&
                filesObject?.[key]?.file?.type.includes('image');
              return addFile(key, filesObject, showImageTooltip);
            }),
          );
          setAttachmentLoading(false);
        } catch (e) {
          toast.error(COMMON_ERROR_MESSAGE);
          setAttachmentLoading(false);
        }
      }
    },
    [
      setThreadInputBoxFiles,
      threadInputBoxFiles,
      addFile,
      setAttachmentLoading,
      convertedFromTextFilesLength,
    ],
  );

  const handleUploadedImageMask = async (maskFile: File) => {
    if (maskFile) {
      try {
        return await addMaskFile(maskFile);
      } catch (e) {
        toast.error(COMMON_ERROR_MESSAGE);
        setAttachmentLoading(false);
      }
    }
  };

  const addMaskFile = async (file: File) => {
    try {
      const result = await getFileUploadUrl({
        user_id,
        filename: file.name.toLocaleLowerCase(),
        content_type: file.type,
        apiContext: {
          user_id,
          conversation_id:
            currentConversationId === DEFAULT_CHAT_ID
              ? ''
              : currentConversationId,
        },
      }).unwrap();
      if (!!result) {
        const formData = new FormData();
        for (const [key, value] of Object.entries(result.form_data.fields)) {
          formData.append(key, value);
        }
        formData.append('file', file);

        const uploadFileUrl = result.form_data?.url;

        try {
          const response = await fetch(uploadFileUrl, {
            method: 'POST',
            body: formData,
            mode: 'cors',
          });

          if (response.ok) {
            return result.filename;
          } else {
            setError(COMMON_ERROR_MESSAGE);
            toast.error(COMMON_ERROR_MESSAGE);
            return;
          }
        } catch (error) {
          setError(COMMON_ERROR_MESSAGE);
          toast.error(COMMON_ERROR_MESSAGE);
          return;
        }
      }
    } catch (e) {
      setError(COMMON_ERROR_MESSAGE);
      toast.error(COMMON_ERROR_MESSAGE);
      return;
    }
  };

  const handleUpTextToFiles = useCallback(
    async (file: File) => {
      if (convertedFromTextFilesLength + 1 > MAX_NUMBER_OF_FILES) {
        toast.error(MAX_PASTED_SIZE_ERROR_MESSAGE);
        return;
      }

      if (file) {
        try {
          setAttachmentLoading(true);

          const fileId = getRandomFileId();
          const fileObject = {
            [fileId]: {
              file: file,
              isLoading: false,
              errorMessage: null,
            },
          };

          setThreadInputBoxFiles({ ...threadInputBoxFiles, ...fileObject });
          await addFile(fileId, fileObject);

          setAttachmentLoading(false);
        } catch (e) {
          toast.error(COMMON_ERROR_MESSAGE);
          setAttachmentLoading(false);
        }
      }
    },
    [
      setThreadInputBoxFiles,
      threadInputBoxFiles,
      addFile,
      setAttachmentLoading,
      convertedFromTextFilesLength,
    ],
  );

  return {
    addFile,
    sendReceivedFile,
    handleUploadedFiles,
    handleUpTextToFiles,
    handleUploadedImageMask,
  };
};
